import { useQuery } from '@tanstack/react-query';

import { baseAPI } from '~shared/api/base-api';
import { KEYS } from '~shared/api/query-keys';

export interface GetParentOutstandingBalanceResponse {
  studioName: string;
  parentFirstName: string;
  parentLastName: string;
  parentEmail: string;
  totalOutstanding: number;
  outstandingBalances: Array<OutstandingBalance>;
  isSingleParent: boolean;
}

export interface OutstandingBalance {
  _id: string;
  studioId: string;
  subscriptionId?: null | string;
  parentId: string;
  studentId: string;
  status: string;
  paymentProvider: string;
  line_items: Array<LineItem>;
  baseAmount: number;
  finalAmount: number;
  dueDate?: Date | null;
  startDate?: Date;
  endDate?: Date;
  metadata: Metadata;
  createdAt: Date;
  updatedAt: Date;
  __v: number;
  paymentMethod?: string | null;
  type?: string;
  appliedCouponId?: string;
  transactionCodeId?: null | string;
}

export interface LineItem {
  name: string;
  amount: number;
  quantity: number;
  type: string;
  total: number;
}

interface RawLineItem extends Omit<LineItem, 'amount' | 'total'> {
  amount?: number | string | null;
  total?: number | string | null;
}

interface RawOutstandingBalance
  extends Omit<
    OutstandingBalance,
    'baseAmount' | 'finalAmount' | 'line_items'
  > {
  line_items?: Array<RawLineItem> | null;
  baseAmount?: number | string | null;
  finalAmount?: number | string | null;
}

interface RawParentOutstandingBalanceResponse
  extends Omit<
    GetParentOutstandingBalanceResponse,
    'totalOutstanding' | 'outstandingBalances'
  > {
  totalOutstanding?: number | string | null;
  outstandingBalances?: Array<RawOutstandingBalance> | null;
}

export interface Metadata {
  attemptCount?: number;
  internalTransactionId?: string;
  bulkPaymentId?: string;
  'metadata.paymentIntentId'?: string;
  paymentIntentId?: string;
  $set?: Set;
}

export interface Set {
  'metadata.paymentIntentId': string;
}

const toFiniteCurrencyNumber = (value: number | string | null | undefined) => {
  const numberValue = Number(value ?? 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

export const reportingParentOutstandingBalancesAPI = async (params: {
  parentId?: string;
  format: string;
}) => {
  const response = await baseAPI.get<
    RawParentOutstandingBalanceResponse | string | null
  >('/reporting/parent-outstanding-balances', {
    params,
    responseType: params.format === 'text' ? 'text' : 'json',
  });

  if (typeof response.data === 'string' || !response.data) {
    return response.data;
  }

  return {
    ...response.data,
    totalOutstanding: toFiniteCurrencyNumber(response.data.totalOutstanding),
    outstandingBalances: (response.data.outstandingBalances ?? []).map(
      (balance) => ({
        ...balance,
        baseAmount: toFiniteCurrencyNumber(balance.baseAmount),
        finalAmount: toFiniteCurrencyNumber(balance.finalAmount),
        line_items: (balance.line_items ?? []).map((lineItem) => ({
          ...lineItem,
          amount: toFiniteCurrencyNumber(lineItem.amount),
          total: toFiniteCurrencyNumber(lineItem.total),
        })),
      }),
    ),
  };
};

export const useReportingParentOutstandingBalances = (params: {
  parentId?: string;
  format: string;
}) => {
  return useQuery({
    queryKey: [
      KEYS.PARENT_OUTSTANDING_BALANCES,
      params.parentId,
      params.format,
    ],
    queryFn: () => reportingParentOutstandingBalancesAPI(params),
    enabled: !!params.format && !!params.parentId,
  });
};
